import { useState, useCallback } from 'react'
import dayjs from 'dayjs'
import UsDateInput from '@components/common/UsDateInput'

const PRESETS = [
  { id: 'today', label: 'Today', fn: () => [dayjs().format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')] },
  { id: 'this-week', label: 'This Week', fn: () => [dayjs().startOf('week').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')] },
  { id: 'this-month', label: 'This Month', fn: () => [dayjs().startOf('month').format('YYYY-MM-DD'), dayjs().endOf('month').format('YYYY-MM-DD')] },
  { id: 'last-month', label: 'Last Month', fn: () => [dayjs().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'), dayjs().subtract(1, 'month').endOf('month').format('YYYY-MM-DD')] },
  { id: 'this-quarter', label: 'This Quarter', fn: () => [dayjs().startOf('quarter').format('YYYY-MM-DD'), dayjs().endOf('quarter').format('YYYY-MM-DD')] },
  { id: 'ytd', label: 'Year to Date', fn: () => [dayjs().startOf('year').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')] },
  { id: 'last-year', label: 'Last Year', fn: () => [dayjs().subtract(1, 'year').startOf('year').format('YYYY-MM-DD'), dayjs().subtract(1, 'year').endOf('year').format('YYYY-MM-DD')] },
  { id: 'custom', label: 'Custom' },
]

const DateRangeFilter = ({ value, onChange }) => {
  const [activePreset, setActivePreset] = useState('this-month')

  const handlePreset = useCallback((preset) => {
    setActivePreset(preset.id)
    if (preset.fn) {
      const [from, to] = preset.fn()
      onChange({ from, to })
    }
  }, [onChange])

  return (
    <div className="space-y-2">
      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Date Range</label>
      {/* 2026-08-26, Himaad UI feedback (ADA). The ACTIVE chip was
          `bg-cb-accent text-white` — white on #479eff measures **2.76:1**, which
          fails SC 1.4.3 outright. That is not a decorative miss: this chip is the
          one control on the page that says WHICH DATE RANGE IS APPLIED TO THE
          MONEY, so a user who cannot read it cannot tell what period the figures
          cover. It now fills with --color-cb-accent-text (#064891), where white
          measures **8.96:1**.

          Deliberately the dark-blue-with-white option rather than the light chip
          language (bg-rc-chip-bg / text-rc-chip-text, 6.2:1) that `.btn-rc.is-active`
          uses. Both were sanctioned; the filled one keeps "applied" reading as the
          strongest thing in the row, lands higher on contrast, and does not turn a
          selected chip into something that could be mistaken for a hover state.

          Note the token NAME reads like a text colour and is being used as a fill.
          That is intentional and has precedent (NotificationCenter.jsx:128, 277):
          --color-cb-accent-text is simply the AA-safe blue, and --color-cb-accent
          is the decorative one.

          Both states now carry a 1px border. The INACTIVE chip's `bg-gray-100`
          is 1.10:1 against the page, so before this the chip had no boundary that
          SC 1.4.11 would count; --color-rc-control is 3.03:1. The active chip
          takes a same-colour border purely so the two states measure the same —
          the trick .btn-cb-primary uses in src/index.css for the same reason.

          Colour and geometry ONLY. No preset label, date format or wire value is
          touched here — date FORMATTING is section C and is not in this batch. */}
      <div className="flex flex-wrap gap-1.5">
        {PRESETS.filter(p => p.id !== 'custom').map((preset) => (
          <button
            key={preset.id}
            onClick={() => handlePreset(preset)}
            className={`px-2.5 py-1 rounded-md border text-xs font-medium transition-colors ${
              activePreset === preset.id
                ? 'border-cb-accent-text bg-cb-accent-text text-white'
                : 'border-rc-control bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {preset.label}
          </button>
        ))}
      </div>
      {/* CC-49824: US-format inputs (MM/DD/YYYY) regardless of browser locale.

          NOT `.input-rc`, and this is the same exception ReportFilterPanel.jsx
          documents: UsDateInput appends its own ` pr-7` to whatever className it
          is handed, to clear the calendar button it absolutely positions at
          right-1. `.input-rc` is unlayered and would override that padding to
          8px, sliding "MM/DD/YYYY" under the calendar icon. Border and radius
          only, which is all SC 1.4.11 and B2 actually ask for — border-gray-200
          was 1.24:1, --color-rc-control is 3.03:1. */}
      <div className="flex items-center gap-2">
        <UsDateInput
          aria-label="Start date"
          value={value?.from || ''}
          onChange={(v) => {
            setActivePreset('custom')
            onChange({ ...value, from: v })
          }}
          className="flex-1 w-full text-sm border border-rc-control rounded-rc-control px-2.5 py-1.5 focus:ring-2 focus:ring-cb-accent/20 focus:border-rc-chip-text"
        />
        <span className="text-xs text-gray-500">to</span>
        <UsDateInput
          aria-label="End date"
          value={value?.to || ''}
          onChange={(v) => {
            setActivePreset('custom')
            onChange({ ...value, to: v })
          }}
          className="flex-1 w-full text-sm border border-rc-control rounded-rc-control px-2.5 py-1.5 focus:ring-2 focus:ring-cb-accent/20 focus:border-rc-chip-text"
        />
      </div>
    </div>
  )
}

export default DateRangeFilter
